'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { ProgressStates } from '../../lib/utils/constants/progressStates';
import { ProgressBarStep } from '../atoms/ProgressBarStep';

const cvaProgressBar = cva('relative flex w-full scale-75 items-center justify-center sm:scale-100');

export interface ProgressBarProps extends InputHTMLAttributes<HTMLDivElement>, VariantProps<typeof cvaProgressBar> {
  length: number;
  current: number;
  onProgressClick?: (index: number) => void;
  /**
   * Optional 1-based step number threshold (matching the rendered step numbers).
   * When set, steps with index > maxClickableStep are rendered non-clickable (no pointer cursor, no click handler).
   */
  maxClickableStep?: number;
}

const getState = (current: number, index: number): (typeof ProgressStates)[number] => {
  if (current < index) {
    return 'pending';
  } else if (current === index) {
    return 'active';
  }
  return 'done';
};

const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ className, length, current, onProgressClick, maxClickableStep, ...props }, ref) => {
    return (
      <div {...props} ref={ref} className={cn(cvaProgressBar(), className)}>
        {Array.from({ length }).map((_, i) => {
          const index = i + 1;
          const isClickable =
            !!onProgressClick && (maxClickableStep === undefined || index <= Math.max(0, maxClickableStep));
          return (
            <div
              key={index}
              className={cn(
                'flex max-w-16 shrink grow items-center justify-center last:grow-0 sm:grow-0',
                isClickable ? 'cursor-pointer' : 'cursor-default',
              )}
              onClick={isClickable ? () => onProgressClick(index) : undefined}
            >
              <ProgressBarStep state={getState(current, index)} step={index} />
              {length !== index && <div className="h-0 min-w-6 max-w-8 shrink grow border border-black-300 sm:w-8" />}
            </div>
          );
        })}
      </div>
    );
  },
);
ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
